import {
  useState,
  useEffect,
  useCallback
} from "react";
import {
  Loader2,
  AlertTriangle,
  Trophy,
  ChevronDown,
  ChevronUp,
  BookOpen
} from "lucide-react";
import {
  useStudentAuth
} from "../../context/StudentAuthContext";
import axios from "axios";
import {
  useToast
} from "../../context/ToastContext";

const API = process.env.REACT_APP_API_URL || "http://localhost:5000";
const axiosInstance = axios.create({ baseURL: API, withCredentials: true });

// Student exam APIs (as per backend endpoints)
const getMyResults = async () => {
  const res = await axiosInstance.get("/student-exam/results");
  return res.data;
};

const getMyResultByExam = async (examId) => {
  const res = await axiosInstance.get(`/student-exam/results/${examId}`);
  return res.data;
};

const TYPE_COLORS = {
  FA: "bg-blue-100 text-blue-700",
  SA: "bg-purple-100 text-purple-700",
  UNIT_TEST: "bg-green-100 text-green-700",
  CLASS_TEST: "bg-yellow-100 text-yellow-800",
  HALF_YEARLY: "bg-orange-100 text-orange-700",
  ANNUAL: "bg-red-100 text-red-700",
  OTHER: "bg-neutral-100 dark:bg-neutral-800 dark:bg-neutral-900 text-neutral-600 dark:text-neutral-300",
};

const rankMedal = (rank) => {
  if (rank === 1) return "🥇";
  if (rank === 2) return "🥈";
  if (rank === 3) return "🥉";
  return rank != null ? `#${rank}` : "–";
};

export default function MyExamResults() {
  const { student } = useStudentAuth() || {};

  const [loading, setLoading] = useState(true);
  const [results, setResults] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [detailLoading, setDetailLoading] = useState(null);
  const [detailsCache, setDetailsCache] = useState({});

  const toast = useToast();
const loadResults = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getMyResults();
      const list = Array.isArray(res)
        ? res
        : res?.data || res?.results || [];
      // Only show published
      const published = list.filter(
        (r) => r.isPublished !== false && r.examId
      );
      setResults(published);
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message || "Failed to load your results"
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadResults();
  }, [loadResults]);

  const toggleExpand = async (result) => {
    const id = result._id;
    if (expandedId === id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(id);

    // If we already have subject breakdown, skip
    if (result.subjectResults?.length || detailsCache[id]) return;

    const examId = result.examId?._id || result.examId;
    if (!examId) return;

    setDetailLoading(id);
    try {
      const res = await getMyResultByExam(examId);
      const detail = res?.data || res || {};
      setDetailsCache((prev) => ({ ...prev, [id]: detail }));
    } catch (err) {
      console.error(err);
      // Keep expanded with whatever we have
    } finally {
      setDetailLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="animate-spin text-indigo-600" size={36} />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-3xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100 flex items-center gap-2">
          <Trophy size={26} className="text-indigo-600" />
          My Results
        </h1>
        <p className="text-neutral-500 dark:text-neutral-400 mt-1">
          View your published exam results
        </p>
      </div>

      

      {results.length === 0 ? (
        <div className="bg-white dark:bg-neutral-900 rounded-2xl shadow-sm border border-neutral-100 dark:border-neutral-800 p-12 text-center">
          <BookOpen size={48} className="mx-auto text-neutral-300 dark:text-neutral-600 mb-4" />
          <p className="text-neutral-500 dark:text-neutral-400 text-lg">No results published yet</p>
          <p className="text-neutral-400 dark:text-neutral-500 text-sm mt-1">
            Your results will appear here once the school publishes them
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {results.map((r) => {
            const exam = r.examId || {};
            const examName = exam.name || r.examName || "Exam";
            const examType = exam.type || r.examType || "OTHER";
            const academicYear =
              exam.academicYear || r.academicYear || "";
            const isExpanded = expandedId === r._id;
            const detail = detailsCache[r._id] || r;
            const subjectResults =
              detail.subjectResults || r.subjectResults || [];

            return (
              <div
                key={r._id}
                className="bg-white dark:bg-neutral-900 rounded-2xl shadow-sm border border-neutral-100 dark:border-neutral-800 overflow-hidden"
              >
                <button
                  type="button"
                  onClick={() => toggleExpand(r)}
                  className="w-full text-left p-5 hover:bg-neutral-50 dark:hover:bg-neutral-800/50 transition"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
                          {examName}
                        </h3>
                        <span
                          className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            TYPE_COLORS[examType] || TYPE_COLORS.OTHER
                          }`}
                        >
                          {examType.replace("_", " ")}
                        </span>
                      </div>
                      {academicYear && (
                        <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-2">
                          Academic Year: {academicYear}
                        </p>
                      )}
                      <div className="flex flex-wrap items-center gap-3 text-sm">
                        <span className="font-semibold text-neutral-900 dark:text-neutral-100">
                          {r.overallPercentage != null
                            ? `${r.overallPercentage}%`
                            : "–"}
                        </span>
                        <span
                          className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            (r.overallPercentage ?? 0) >= 80
                              ? "bg-emerald-100 text-emerald-700"
                              : (r.overallPercentage ?? 0) >= 60
                              ? "bg-blue-100 text-blue-700"
                              : (r.overallPercentage ?? 0) >= 33
                              ? "bg-yellow-100 text-yellow-800"
                              : "bg-red-100 text-red-700"
                          }`}
                        >
                          {r.overallGrade || "–"}
                        </span>
                        {(r.rankInSection || r.rankInClass || r.rank) && (
                          <span className="text-neutral-600 dark:text-neutral-300">
                            Rank:{" "}
                            {rankMedal(
                              r.rankInSection ?? r.rankInClass ?? r.rank
                            )}
                          </span>
                        )}
                        <span
                          className={
                            r.isPassed
                              ? "text-green-600 font-medium"
                              : "text-danger-600 dark:text-danger-500 font-medium"
                          }
                        >
                          {r.isPassed ? "✓ Pass" : "✗ Fail"}
                        </span>
                      </div>
                    </div>
                    <div className="shrink-0 text-neutral-400 dark:text-neutral-500 pt-1">
                      {isExpanded ? (
                        <ChevronUp size={20} />
                      ) : (
                        <ChevronDown size={20} />
                      )}
                    </div>
                  </div>
                </button>

                {isExpanded && (
                  <div className="border-t border-neutral-100 dark:border-neutral-800 px-5 pb-5">
                    {detailLoading === r._id ? (
                      <div className="flex justify-center py-8">
                        <Loader2
                          className="animate-spin text-indigo-600"
                          size={24}
                        />
                      </div>
                    ) : subjectResults.length === 0 ? (
                      <p className="text-sm text-neutral-400 dark:text-neutral-500 py-4 text-center">
                        Subject-wise breakdown not available
                      </p>
                    ) : (
                      <div className="overflow-x-auto mt-3">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="text-left text-neutral-500 dark:text-neutral-400 border-b border-neutral-100 dark:border-neutral-800">
                              <th className="pb-2 font-medium">Subject</th>
                              <th className="pb-2 font-medium text-center">
                                Max
                              </th>
                              <th className="pb-2 font-medium text-center">
                                Obtained
                              </th>
                              <th className="pb-2 font-medium text-center">
                                %
                              </th>
                              <th className="pb-2 font-medium text-center">
                                Grade
                              </th>
                              <th className="pb-2 font-medium text-center">
                                Result
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {subjectResults.map((sr, idx) => (
                              <tr
                                key={sr.subjectId?._id || sr.subjectId || idx}
                                className="border-b border-gray-50 last:border-0"
                              >
                                <td className="py-2.5 font-medium text-neutral-900 dark:text-neutral-100">
                                  {sr.subjectName ||
                                    sr.subjectId?.name ||
                                    "Subject"}
                                  {sr.subjectCode && (
                                    <span className="text-xs text-neutral-400 dark:text-neutral-500 ml-1">
                                      ({sr.subjectCode})
                                    </span>
                                  )}
                                </td>
                                <td className="py-2.5 text-center text-neutral-600 dark:text-neutral-300">
                                  {sr.maxMarks ?? "–"}
                                </td>
                                <td className="py-2.5 text-center">
                                  {sr.isAbsent
                                    ? "Ab"
                                    : sr.marksObtained ?? "–"}
                                </td>
                                <td className="py-2.5 text-center">
                                  {sr.isAbsent
                                    ? "–"
                                    : sr.percentage != null
                                    ? `${sr.percentage}%`
                                    : "–"}
                                </td>
                                <td className="py-2.5 text-center">
                                  <span
                                    className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                                      (sr.percentage ?? 0) >= 80
                                        ? "bg-emerald-100 text-emerald-700"
                                        : (sr.percentage ?? 0) >= 60
                                        ? "bg-blue-100 text-blue-700"
                                        : (sr.percentage ?? 0) >= 33
                                        ? "bg-yellow-100 text-yellow-800"
                                        : "bg-red-100 text-red-700"
                                    }`}
                                  >
                                    {sr.isAbsent ? "Ab" : sr.grade || "–"}
                                  </span>
                                </td>
                                <td className="py-2.5 text-center">
                                  {sr.isAbsent ? (
                                    <span className="text-neutral-400 dark:text-neutral-500">Absent</span>
                                  ) : sr.isPassed ? (
                                    <span className="text-green-600">✓</span>
                                  ) : (
                                    <span className="text-danger-600 dark:text-danger-500">✗</span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                          {/* Overall row */}
                          <tfoot>
                            <tr className="border-t border-neutral-200 dark:border-neutral-800 font-semibold">
                              <td className="pt-3">Overall</td>
                              <td className="pt-3 text-center">
                                {detail.totalMaxMarks ?? r.totalMaxMarks ?? "–"}
                              </td>
                              <td className="pt-3 text-center">
                                {detail.totalMarksObtained ??
                                  r.totalMarksObtained ??
                                  "–"}
                              </td>
                              <td className="pt-3 text-center">
                                {(detail.overallPercentage ??
                                  r.overallPercentage) != null
                                  ? `${
                                      detail.overallPercentage ??
                                      r.overallPercentage
                                    }%`
                                  : "–"}
                              </td>
                              <td className="pt-3 text-center">
                                {detail.overallGrade ?? r.overallGrade ?? "–"}
                              </td>
                              <td className="pt-3 text-center">
                                {(detail.isPassed ?? r.isPassed) ? (
                                  <span className="text-green-600">✓ Pass</span>
                                ) : (
                                  <span className="text-danger-600 dark:text-danger-500">✗ Fail</span>
                                )}
                              </td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}